import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { useState } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';

import { WorkLog } from './WorkLog';
import { LoadingState as AnalysisLoadingState } from '../../common/LoadingState';
import { AnalysisResults } from './AnalysisResults';
import { useStyles } from '../../styles';
import { QeScoreAnalysisResults } from '../../interfaces';
import { formatGeneratedDate } from '../../utils';
import { TABS } from '../../constants';

interface QEScoreAnalysisProps {
  isStreamingLoading: boolean;
  isTriggering: boolean;
  isAnalysisLoading: boolean;
  showAnalysisPanel: boolean;
  activeTab: (typeof TABS)[keyof typeof TABS];
  onAnalysisResultsTabClick: () => void;
  onWorkLogTabClick: () => void;
  showAnalysisResults: boolean;
  analysisResults: QeScoreAnalysisResults | null;
  isDownloadingReport: boolean;
  onDownloadReportClick: () => void;
}

export function QEScoreAnalysis({
  isStreamingLoading,
  isTriggering,
  isAnalysisLoading,
  showAnalysisPanel,
  activeTab,
  onAnalysisResultsTabClick,
  onWorkLogTabClick,
  showAnalysisResults,
  analysisResults,
  isDownloadingReport,
  onDownloadReportClick,
}: QEScoreAnalysisProps) {
  const classes = useStyles();

  const [workLogSearchText, setWorkLogSearchText] = useState('');

  function renderAnalysisResultsContent() {
    if (isStreamingLoading) {
      return (
        <AnalysisLoadingState primaryText="Analyzing QE Score...almost there" />
      );
    }
    if (isAnalysisLoading) {
      return (
        <AnalysisLoadingState primaryText="Fetching analysis results..." />
      );
    }
    if (showAnalysisResults) {
      return <AnalysisResults qeScoreAnalysisResults={analysisResults} />;
    }
    return null;
  }

  function renderWorkLogContent() {
    const hasNoEventsYet = !analysisResults?.executionLogs?.events?.length;
    if (isTriggering || (isStreamingLoading && hasNoEventsYet)) {
      return (
        <AnalysisLoadingState
          primaryText="Starting analysis agent..."
          secondaryText="The agent is spinning up. This may take a minute before logs appear."
        />
      );
    }
    return (
      <WorkLog
        isGenerating={isStreamingLoading}
        events={analysisResults?.executionLogs?.events || []}
        searchText={workLogSearchText}
      />
    );
  }

  return (
    <>
      {/* Tabs Section - shown during and after analysis */}
      {showAnalysisPanel && (
        <Paper variant="elevation" className={classes.indexTabsPaper}>
          <Box className={classes.indexReportInfoRow}>
            <Typography variant="body2" className={classes.indexReportInfoText}>
              {analysisResults?.reportMetadata?.generatedOn ? (
                <>
                  Last Analyzed on{' '}
                  <span className={classes.indexReportInfoText}>
                    {formatGeneratedDate(
                      analysisResults.reportMetadata.generatedOn,
                    )}{' '}
                    (UTC)
                  </span>
                </>
              ) : null}
            </Typography>
          </Box>
          {/* Tab Navigation */}
          <Box
            display="flex"
            alignItems="stretch"
            justifyContent="space-between"
            flexWrap="wrap"
            className={classes.indexTabsHeader}
          >
            <Box display="flex" flexWrap="wrap" alignItems="stretch">
              <Button
                onClick={onAnalysisResultsTabClick}
                aria-disabled={isStreamingLoading}
                className={`h-full px-4 py-3 text-sm font-medium transition-colors ${
                  isStreamingLoading ? classes.indexTabButtonBlocked : ''
                } ${classes.indexTabButtonBase} ${
                  activeTab === TABS.ANALYSIS_RESULTS
                    ? classes.indexTabButtonActive
                    : classes.indexTabButtonInactive
                }`}
              >
                Analysis results
              </Button>
              <Button
                onClick={onWorkLogTabClick}
                disabled={isStreamingLoading}
                className={`h-full px-4 py-3 text-sm font-medium transition-colors ${
                  isStreamingLoading ? 'cursor-not-allowed opacity-50' : ''
                } ${classes.indexTabButtonBase} ${
                  activeTab === TABS.WORK_LOG
                    ? classes.indexTabButtonActive
                    : classes.indexTabButtonInactive
                }`}
              >
                Work Log
              </Button>
            </Box>

            {/* Search - visible only on Work Log tab */}
            {activeTab === TABS.WORK_LOG && !isStreamingLoading && (
              <Box
                display="flex"
                alignItems="center"
                gridGap={8}
                px={2}
                flexWrap="wrap"
              >
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search Work logs"
                  className={classes.indexWorkLogSearch}
                  value={workLogSearchText}
                  onChange={event => setWorkLogSearchText(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon className={classes.indexSearchIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* Download report - visible only after analysis results load */}
            {activeTab === TABS.ANALYSIS_RESULTS && showAnalysisResults && (
              <Box
                display="flex"
                alignItems="center"
                gridGap={8}
                px={2}
                flexWrap="wrap"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  classes={{
                    root: classes.indexDownloadButton,
                    label: `${classes.flex} ${classes.gap1}`,
                  }}
                  size="large"
                  disabled={isDownloadingReport}
                  onClick={onDownloadReportClick}
                >
                  {isDownloadingReport ? (
                    <>
                      <CircularProgress size={16} color="inherit" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <GetAppIcon />
                      <span>Download report</span>
                    </>
                  )}
                </Button>
              </Box>
            )}
          </Box>

          {/* Tab Content */}
          <Box p={3}>
            {activeTab === TABS.ANALYSIS_RESULTS &&
              renderAnalysisResultsContent()}
            {activeTab === TABS.WORK_LOG && renderWorkLogContent()}
          </Box>
        </Paper>
      )}
    </>
  );
}
